import { subscribeAppResume } from "../services/appResumeService";
/**
 * useFamily.js — Backend API versiyonu
 * Firestore tamamen kaldırıldı. useAuth'tan gelen user/profil kullanılıyor.
 */
import { useState, useRef } from "react";
import { apiRequest, saveSession } from "../services/apiClient";
import { rt } from "../i18n/runtime";
import { adminPermissions, defaultPermissions } from "../utils/permissions";
import {
  aileOlusturService,
  aileyeKatilService,
  ailedenAyrilService,
  borcHesabiDegistirService,
} from "../services/familyService";
import { appAlert, appConfirm } from "../components/AppDialog";
import { startPresence } from "../services/presenceService";
import {
  uyeYetkiDegistirService,
  uyeRolDegistirService,
  uyeyiAiledenCikarService,
  yetkiKaydetService,
} from "../services/userService";
import {
  aktifAileDegistirService,
  kullaniciFamilileriniGetir,
} from "../services/aileSwitcherService";
import { logAnalyticsEvent } from "../services/analyticsService";



const isFamilyAdminRole = (role) => {
  const text = String(role || "").trim().toLowerCase();
  return ["aile yöneticisi", "aile yoneticisi", "family admin", "admin", "owner", "yönetici", "yonetici"].includes(text);
};

const normalizeFamilyMemberRole = (role) => {
  const raw = String(role || "").trim();
  if (["Toplu Taşıma", "Public Transport", "Transportation"].includes(raw)) return "Çocuk";
  return raw;
};

const normalizeFamilyMember = (member) => {
  if (!member) return member;
  const normalizedRole = normalizeFamilyMemberRole(member.rol || member.profilRol);
  return normalizedRole ? { ...member, rol: normalizedRole, profilRol: normalizedRole } : member;
};

const normalizeFamilyMembers = (members) => (Array.isArray(members) ? members.map(normalizeFamilyMember) : []);
const normalizeUid = (value) => String(value || "").trim();

const mergePresenceIntoMembers = (members, presenceUsers, currentUid) => {
  if (!Array.isArray(members)) return [];
  const byUid = new Map();
  (presenceUsers || []).forEach((p) => {
    const uid = normalizeUid(p.uid || p.id || p.firebaseUid || p.userId);
    if (uid) byUid.set(uid, p);
  });
  const selfUid = normalizeUid(currentUid);
  return members.map((m) => {
    const uid = normalizeUid(m.uid || m.id || m.firebaseUid || m.userId);
    const p = byUid.get(uid);
    const isSelf = uid && selfUid && uid === selfUid;
    return {
      ...m,
      isOnline: isSelf ? false : !!(p?.isOnline ?? p?.online ?? m.isOnline ?? m.online),
      online: isSelf ? false : !!(p?.isOnline ?? p?.online ?? m.online ?? m.isOnline),
      lastActiveAt: p?.lastActiveAt || p?.lastSeenAt || m.lastActiveAt || m.lastSeenAt || null,
      lastSeenAt: p?.lastSeenAt || p?.lastActiveAt || m.lastSeenAt || m.lastActiveAt || null,
    };
  });
};


export function useFamily({ user, profil, setProfil, setAktifSayfa }) {
  // Cache'den anında başlat — API gelmeden önce son veriler gösterilsin
  const currentUserUid = String(user?.uid || user?.firebase_uid || user?.id || "").trim();
  const _fc = (() => {
    try {
      const r = localStorage.getItem("fp:familyCache");
      const cache = r ? JSON.parse(r) : null;
      return cache && currentUserUid && String(cache?.userUid || "").trim() === currentUserUid ? cache : null;
    } catch { return null; }
  })();
  const [familyId, setFamilyId]       = useState(_fc?.familyId || null);
  const [familyContextUserUid, setFamilyContextUserUid] = useState(
    _fc?.familyId ? currentUserUid : ""
  );
  const [familyName, setFamilyName]   = useState(_fc?.familyName || "");
  const [familyCode, setFamilyCode]   = useState(_fc?.familyCode || "");
  const [ownerUid, setOwnerUid]       = useState(_fc?.ownerUid || "");
  const [loadingFamily, setLoadingFamily] = useState(!_fc);
  const [membersLoading, setMembersLoading] = useState(!(_fc && Array.isArray(_fc.uyeler)));
  const [familyResolved, setFamilyResolved] = useState(!!_fc);
  const [familyLoadError, setFamilyLoadError] = useState("");
  const familyLoadSeqRef = useRef(0);
  const familyTransitionRef = useRef(new Map());
  const activeFamilyRef = useRef({
    familyId: _fc?.familyId || null,
    familyName: _fc?.familyName || "",
    familyCode: _fc?.familyCode || "",
    ownerUid: _fc?.ownerUid || "",
  });

  const [seciliAileEkrani, setSeciliAileEkrani] = useState("");
  const [aileAdi, setAileAdi]         = useState("");
  const [katilmaKodu, setKatilmaKodu] = useState("");
  const [aileHata, setAileHata]       = useState("");
  const [basariMesaji, setBasariMesaji] = useState("");

  const [uyeler, setUyeler]           = useState(() => _fc?.uyeler || []);
  const uyelerRef                     = useRef(_fc?.uyeler || []);
  const presenceUsersRef              = useRef([]);
  // Aynı aile için eşzamanlı /members çağrılarını tek HTTP isteğinde birleştir.
  // Çağrı noktaları korunur; yalnızca devam eden Promise paylaşılır.
  const membersRequestRef             = useRef(new Map());
  const membersLoadedFamilyRef        = useRef(
    _fc?.familyId && Array.isArray(_fc?.uyeler) ? String(_fc.familyId) : ""
  );

  const fetchMembersDeduped = (fId) => {
    const key = String(fId || "").trim();
    if (!key) return Promise.resolve({ members: [] });

    const existing = membersRequestRef.current.get(key);
    if (existing) return existing;

    const request = apiRequest(`/families/${key}/members`)
      .finally(() => {
        if (membersRequestRef.current.get(key) === request) {
          membersRequestRef.current.delete(key);
        }
      });

    membersRequestRef.current.set(key, request);
    return request;
  };
  const [seciliUye, setSeciliUye]     = useState(null);
  const [uyelerAcik, setUyelerAcik]   = useState(() => {
    try { const s = localStorage.getItem("uyelerAcik"); return s === null ? false : JSON.parse(s); }
    catch { return false; }
  });
  const [seciliYetkiUye, setSeciliYetkiUye] = useState(null);
  const [borcHesabi, setBorcHesabi]   = useState(_fc?.borcHesabi || false);

  /* ── Aile verisini backend'den yükle ── */

  // Offline fallback: başarılı yüklemede aile snapshot'ını localStorage'a yaz
  const FAMILY_CACHE_KEY = "fp:familyCache";
  const saveFamilyCache = (familyData) => {
    try { localStorage.setItem(FAMILY_CACHE_KEY, JSON.stringify(familyData)); } catch {}
  };
  const loadFamilyCache = () => {
    try { const r = localStorage.getItem(FAMILY_CACHE_KEY); return r ? JSON.parse(r) : null; }
    catch { return null; }
  };

  const commitFamilySnapshot = (family, families = null) => {
    const fId = String(family?.familyId || family?.id || "").trim();
    if (!fId) throw new Error("Aile kimliği bulunamadı.");
    const snapshot = {
      familyId: fId,
      familyName: family?.familyName || family?.name || "",
      familyCode: family?.familyCode || family?.code || "",
      ownerUid: family?.ownerUid || "",
      borcHesabi: !!family?.borcHesabi,
      rol: family?.rol || family?.role || "Aile Üyesi",
      permissions: family?.permissions || defaultPermissions,
    };
    const familyList = Array.isArray(families) ? families : (profil?.families || []);

    const previousId = activeFamilyRef.current.familyId;
    activeFamilyRef.current = snapshot;
    if (String(previousId || "") !== fId) {
      // Aile kimliği commit edildiği anda önceki ailenin üyelerini göstermeyi bırak.
      // Yeni liste, zaten familyId effect'inin başlattığı tek mevcut /members
      // isteğiyle doldurulur; burada ek istek veya polling başlatılmaz.
      presenceUsersRef.current = [];
      uyelerRef.current = [];
      membersLoadedFamilyRef.current = "";
      setUyeler([]);
      setMembersLoading(true);
    }
    setFamilyId(snapshot.familyId);
    setFamilyContextUserUid(currentUserUid);
    setFamilyName(snapshot.familyName);
    setFamilyCode(snapshot.familyCode);
    setOwnerUid(snapshot.ownerUid);
    setBorcHesabi(snapshot.borcHesabi);
    setFamilyResolved(true);
    try { localStorage.setItem("fp:aktivFamilyId", snapshot.familyId); } catch {}
    setProfil((p) => ({
      ...(p || {}),
      familyId: snapshot.familyId,
      aktivFamilyId: snapshot.familyId,
      families: familyList.length ? familyList : (p?.families || []),
      aileYetkisi: snapshot.rol,
      permissions: snapshot.permissions,
    }));
    saveFamilyCache({
      userUid: String(user?.uid || user?.firebase_uid || user?.id || currentUserUid).trim(),
      ...snapshot,
      families: familyList,
      uyeler: previousId === fId ? uyelerRef.current : [],
    });
    return snapshot;
  };

  const switchFamily = async (targetFamilyId, options = {}) => {
    const targetId = String(targetFamilyId || "").trim();
    if (!targetId) throw new Error("Hedef aile bulunamadı.");
    const reason = options.reason || "manual";
    const key = `${reason}:${targetId}`;
    const existing = familyTransitionRef.current.get(key);
    if (existing) return existing;

    const transition = (async () => {
      let families = Array.isArray(options.families) ? options.families : null;
      if (!families?.length) families = await kullaniciFamilileriniGetir();
      const matchesTarget = (family) => [
        family?.familyId,
        family?.id,
        family?.pgFamilyId,
        family?.pg_family_id,
        family?.databaseFamilyId,
      ].some((candidate) => String(candidate || "").trim() === targetId);
      const target = options.family || families.find((f) =>
        matchesTarget(f)
      );
      if (!target || !families.some(matchesTarget)) {
        throw new Error("Bu aileye erişiminiz bulunmuyor.");
      }
      const canonicalTargetId = String(target.familyId || target.id || "").trim();
      if (!canonicalTargetId) throw new Error("Hedef aile kimliği bulunamadı.");

      const previousFamilyId = activeFamilyRef.current.familyId || familyId || null;
      const callTransition = reason === "call-answer";
      if (!callTransition && !options.backendAlreadyPersisted && previousFamilyId !== canonicalTargetId) {
        await aktifAileDegistirService({ familyId: canonicalTargetId, families, currentFamilyId: previousFamilyId });
      }

      // Event-driven transition startup hydration'dan daha yenidir. Daha önce
      // başlamış /families/my veya members sonucu hedef aileyi eski snapshot'la
      // geri ezmemelidir.
      familyLoadSeqRef.current += 1;
      const snapshot = commitFamilySnapshot(target, families);
      setLoadingFamily(false);
      setFamilyResolved(true);
      if (callTransition && !options.backendAlreadyPersisted && previousFamilyId !== canonicalTargetId) {
        snapshot.persistencePromise = aktifAileDegistirService({
          familyId: canonicalTargetId,
          families,
          currentFamilyId: previousFamilyId,
        }).then(() => true).catch(() => false);
      }
      // Call Answer yalnız call context'ini bekler; dashboard üyeleri WebRTC'yi
      // geciktirmemeli. Normal/manual/notification transition ise zaten App
      // effect'inin yaptığı tek /members isteğini burada awaitable hale getirir.
      if (!callTransition) {
        snapshot.membersPromise = uyeleriYenile(canonicalTargetId);
        await snapshot.membersPromise;
      }
      return snapshot;
    })().finally(() => familyTransitionRef.current.delete(key));

    familyTransitionRef.current.set(key, transition);
    return transition;
  };

  const aileBilgisiYukle = async (currentUser, prefetchedUserData = null) => {
    const loadSeq = ++familyLoadSeqRef.current;
    const requestUid = String(
      currentUser?.uid || currentUser?.firebase_uid || currentUser?.id || ""
    ).trim();

    setLoadingFamily(true);
    setMembersLoading(true);
    setFamilyLoadError("");

    const cache = loadFamilyCache();
    const cacheUid = String(cache?.userUid || "").trim();
    const matchingCache = cache && requestUid && cacheUid === requestUid ? cache : null;

    const applyMatchingCache = () => {
      if (!matchingCache) return false;
      activeFamilyRef.current = {
        familyId: matchingCache.familyId || null,
        familyName: matchingCache.familyName || "",
        familyCode: matchingCache.familyCode || "",
        ownerUid: matchingCache.ownerUid || "",
      };
      setFamilyId(matchingCache.familyId || null);
      setFamilyContextUserUid(requestUid);
      setFamilyName(matchingCache.familyName || "");
      setFamilyCode(matchingCache.familyCode || "");
      setOwnerUid(matchingCache.ownerUid || "");
      setBorcHesabi(!!matchingCache.borcHesabi);
      const cachedMembers = Array.isArray(matchingCache.uyeler) ? matchingCache.uyeler : [];
      uyelerRef.current = cachedMembers;
      membersLoadedFamilyRef.current = matchingCache.familyId && Array.isArray(matchingCache.uyeler)
        ? String(matchingCache.familyId)
        : "";
      setUyeler(cachedMembers);
      setMembersLoading(false);
      setProfil((p) => ({
        ...(p || {}),
        uid: currentUser?.uid || currentUser?.firebase_uid || currentUser?.id || p?.uid,
        email: currentUser?.email || p?.email,
        familyId: matchingCache.familyId || null,
        families: matchingCache.families || p?.families || [],
        aileYetkisi: matchingCache.rol || p?.aileYetkisi || "Aile Üyesi",
        permissions: matchingCache.permissions || p?.permissions || defaultPermissions,
      }));
      setFamilyResolved(true);
      // Cache'de üyeler varsa popup için kullanılabilir; backend yine arka planda yeniler.
      setMembersLoading(cachedMembers.length === 0);
      return true;
    };

    // Geçerli cache varsa API sonucu gelene kadar mevcut aile ekranını koru.
    // Böylece kısa süreli backend/refresh gecikmesinde "Aile grubun yok" ekranı gösterilmez.
    applyMatchingCache();

    try {
      const backendAktivId = currentUser?.aktivFamilyId
        || currentUser?.profil?.aktivFamilyId
        || prefetchedUserData?.aktivFamilyId
        || null;

      let storedAktivId = null;
      try { storedAktivId = localStorage.getItem("fp:aktivFamilyId"); } catch {}

      const data = await apiRequest("/families/my");
      if (loadSeq !== familyLoadSeqRef.current) return;

      const families = Array.isArray(data?.families) ? data.families : [];
      const storedIsValid = families.some((f) =>
        String(f.familyId || f.id) === String(storedAktivId || "")
      );
      const aktivId = storedIsValid ? storedAktivId : backendAktivId;

      // "Ailesi yok" kararı yalnızca başarılı ve kesin backend cevabıyla verilir.
      if (families.length === 0) {
        setFamilyId(null); setFamilyName(""); setFamilyCode("");
        setFamilyContextUserUid(requestUid);
        setOwnerUid(""); setUyeler([]); uyelerRef.current = [];
        try {
          localStorage.removeItem("fp:familyCache");
          localStorage.removeItem("fp:aktivFamilyId");
        } catch {}
        setProfil((p) => ({
          ...(p || {}),
          uid: currentUser?.uid || currentUser?.firebase_uid || currentUser?.id || p?.uid,
          email: currentUser?.email || p?.email,
          displayName: currentUser?.display_name || currentUser?.displayName || p?.displayName,
          familyId: null,
          families: [],
        }));
        setFamilyResolved(true);
        setMembersLoading(false);
        return;
      }

      const safeTime = (f) => {
        const t = new Date(f.joinedAt || f.createdAt || "").getTime();
        return isNaN(t) ? 9999999999999 : t;
      };
      const sortedFamilies = [...families].sort((a, b) => safeTime(a) - safeTime(b));

      const aktif = families.find(f => String(f.familyId || f.id) === String(aktivId))
                 || sortedFamilies[0]
                 || families[0];
      const fId = aktif.familyId || aktif.id;

      activeFamilyRef.current = {
        familyId: fId,
        familyName: aktif.familyName || aktif.name || "",
        familyCode: aktif.familyCode || aktif.code || "",
        ownerUid: aktif.ownerUid || "",
      };

      setFamilyId(fId);
      setFamilyContextUserUid(requestUid);
      setFamilyName(aktif.familyName || aktif.name || "");
      setFamilyCode(aktif.familyCode || aktif.code || "");
      setOwnerUid(aktif.ownerUid || "");
      setBorcHesabi(!!aktif.borcHesabi);
      try { localStorage.setItem("fp:aktivFamilyId", fId); } catch {}

      setProfil((p) => ({
        ...(p || {}),
        familyId: fId,
        families,
        aileYetkisi: aktif.rol || aktif.role || "Aile Üyesi",
        permissions: aktif.permissions || defaultPermissions,
      }));

      // Cache'deki üyeleri geçici olarak uygula; arka plan fetch tamamlanınca güncellenir
      // let: IIFE içinde members gelince reassign edilir
      let uyeList = matchingCache?.familyId === fId && Array.isArray(matchingCache?.uyeler)
        ? matchingCache.uyeler
        : [];
      uyelerRef.current = uyeList;
      membersLoadedFamilyRef.current = String(fId);
      setUyeler(uyeList);

      // /families/my tamamlandı → familyId belli, state güncellendi.
      // familyResolved burada set et ki girisYap callback'i hemen devam edebilsin.
      // Üye fetch'i arka planda tamamlanacak; tamamlanınca cache ve uyeler güncellenecek.
      setFamilyResolved(true);

      // Üye fetch'ini arka planda çalıştır — bu await return'ü bloke etmez
      (async () => {
        try {
          const mData = await fetchMembersDeduped(fId);
          if (loadSeq !== familyLoadSeqRef.current) return;
          uyeList = mergePresenceIntoMembers(
            normalizeFamilyMembers(mData.members || []),
            presenceUsersRef.current,
            currentUser?.uid || currentUser?.id
          );
        } catch (memberError) {
          console.warn("[useFamily] üyeler arka planda yenilenemedi:", memberError?.message);
        }
        uyelerRef.current = uyeList;
        setUyeler(uyeList);
        setMembersLoading(false);
        saveFamilyCache({
          userUid: requestUid,
          familyId: fId,
          familyName: aktif.familyName || aktif.name || "",
          familyCode: aktif.familyCode || aktif.code || "",
          ownerUid: aktif.ownerUid || "",
          borcHesabi: !!aktif.borcHesabi,
          families,
          rol: aktif.rol || aktif.role || "Aile Üyesi",
          permissions: aktif.permissions || defaultPermissions,
          uyeler: uyeList,
        });
      })();

      // fId'yi return et — girisYap callback'i bunu kullanarak
      // veriDinlemeBaslat'ı members beklenmeden hemen başlatır
      return fId;
    } catch (e) {
      if (loadSeq !== familyLoadSeqRef.current) return;
      console.warn("[useFamily] aileBilgisiYukle hata:", e.message);

      // Her türlü geçici API/refresh/429/5xx hatasında eşleşen aile cache'ini koru.
      // Sadece başarılı "/families/my -> []" cevabı ailesiz ekranını açabilir.
      if (applyMatchingCache()) {
        setFamilyLoadError("");
        return;
      }

      // Cache yoksa yanlışlıkla "ailesi yok" kararı verme.
      // Mevcut state varsa onu koru; yoksa güvenli yükleme ekranında kal.
      if (familyId) {
        setFamilyResolved(true);
      } else {
        setFamilyResolved(false);
        setFamilyLoadError(e?.message || "Aile bilgileri yüklenemedi.");
      }

      setProfil((p) => ({
        ...(p || {}),
        uid: currentUser?.uid || currentUser?.firebase_uid || currentUser?.id || p?.uid,
        email: currentUser?.email || p?.email,
        displayName: currentUser?.display_name || currentUser?.displayName || p?.displayName,
        familyId: p?.familyId ?? null,
        families: p?.families || [],
      }));
    } finally {
      if (loadSeq === familyLoadSeqRef.current) setLoadingFamily(false);
    }
  };

  /* ── Aile sıfırla (çıkış) ── */
  const aileSifirla = ({ clearCache = true } = {}) => {
    familyLoadSeqRef.current += 1;
    activeFamilyRef.current = {
      familyId: null,
      familyName: "",
      familyCode: "",
      ownerUid: "",
    };
    familyTransitionRef.current.clear();
    membersRequestRef.current.clear();
    membersLoadedFamilyRef.current = "";
    presenceUsersRef.current = [];
    setFamilyId(null); setFamilyName(""); setFamilyCode(""); setOwnerUid("");
    setFamilyContextUserUid("");
    setUyeler([]); uyelerRef.current = []; setLoadingFamily(false);
    setMembersLoading(false);
    setFamilyResolved(false); setFamilyLoadError("");
    try {
      localStorage.removeItem("fp:aktivFamilyId");
      if (clearCache) localStorage.removeItem("fp:familyCache");
    } catch {}
  };

  const uyeleriYenile = async (fId = familyId, { silent = true } = {}) => {
    if (!fId) return [];
    try {
      const data = await fetchMembersDeduped(fId);
      const uyeList = mergePresenceIntoMembers(normalizeFamilyMembers(data.members || []), presenceUsersRef.current, user?.uid || user?.id);
      // Eski aileye ait gecikmiş bir cevap, transition tamamlandıktan sonra yeni
      // ailenin kartını geri ezmemeli.
      if (String(activeFamilyRef.current.familyId || "") !== String(fId)) {
        return uyeList;
      }
      uyelerRef.current = uyeList;
      membersLoadedFamilyRef.current = String(fId);
      setUyeler(uyeList);
      const currentMember = uyeList.find((member) => normalizeUid(member?.uid || member?.id) === currentUserUid);
      if (currentMember?.healthProfile) {
        setProfil((current) => ({
          ...(current || {}),
          healthProfile: currentMember.healthProfile,
        }));
      }
      setMembersLoading(false);
      const cache = loadFamilyCache();
      if (String(cache?.familyId || "") === String(fId)) {
        saveFamilyCache({ ...cache, uyeler: uyeList });
      }
      return uyeList;
    } catch (e) {
      if (!silent) throw e;
      console.warn("[useFamily] üyeler yenilenemedi:", e.message);
      if (String(activeFamilyRef.current.familyId || "") === String(fId)) {
        setMembersLoading(false);
      }
      return uyelerRef.current || [];
    }
  };

  /* ── Üyeleri backend'den dinle (başlangıç + resume + SSE değişikliği) ── */
  const uyelerDinle = (fId) => {
    if (!fId) return () => {};
    let iptal = false;

    const yukle = async () => {
      if (iptal) return;
      await uyeleriYenile(fId);
    };

    // Transition aynı aileyi biraz önce başarıyla yüklediyse ikinci bir başlangıç
    // GET'i atma. Presence ve mevcut resume/seyrek yenileme davranışı korunur.
    if (membersLoadedFamilyRef.current !== String(fId)) yukle();

    const stopPresence = startPresence({
      familyId: fId,
      user,
      onUpdate: (presenceUsers) => {
        presenceUsersRef.current = presenceUsers || [];
        const merged = mergePresenceIntoMembers(uyelerRef.current || [], presenceUsersRef.current, user?.uid || user?.id);
        uyelerRef.current = merged;
        setUyeler(merged);
      },
    });

    const unsubscribeResume = subscribeAppResume(yukle, { delayMs: 450 });
    const permissionsChanged = (event) => {
      if (String(event?.detail?.familyId || "") === String(fId)) yukle();
    };
    window.addEventListener("fp:familyPermissionsChanged", permissionsChanged);
    const reconcileData = (event) => {
      if (String(event?.detail?.familyId || "") === String(fId)) yukle();
    };
    window.addEventListener("fp:dataReconcile", reconcileData);

    return () => {
      iptal = true;
      stopPresence?.();
      unsubscribeResume();
      window.removeEventListener("fp:familyPermissionsChanged", permissionsChanged);
      window.removeEventListener("fp:dataReconcile", reconcileData);
    };
  };

  /* ── Aile oluştur / katıl sonrası tek seferlik senkronizasyon ──
     Polling yok. Yalnızca üyelik değiştiren başarılı işlemden sonra çalışır.
     /auth/me ile güncel profil alınır; ardından aile snapshot'ı tek sefer yenilenir.
     Böylece profil popup'ı, aktif aile, rol ve aile cache'i kill/restart beklemeden güncellenir. */
  const aileUyelikSonrasiSenkronizeEt = async (sonuc) => {
    try { localStorage.setItem("fp:aktivFamilyId", sonuc.familyId); } catch {}
    const currentFamilies = Array.isArray(profil?.families) ? profil.families : [];
    const nextFamilies = currentFamilies.some((family) => String(family?.familyId || family?.id || "") === String(sonuc.familyId))
      ? currentFamilies
      : [...currentFamilies, sonuc];

    // Create/join endpoint'i başarılı döndüğünde üyelik işlemi tamamlanmıştır.
    // İkincil /auth/me isteğini UI doğruluğunun ön koşulu yapmak, geçici bir ağ
    // gecikmesinde splash'i sonsuza kadar açık bırakıyor ve kullanıcıyı aynı
    // işlemi yeniden yapmaya zorluyordu. Canonical sonucu hemen commit et.
    familyLoadSeqRef.current += 1;
    commitFamilySnapshot(sonuc, nextFamilies);
    setLoadingFamily(false);
    setFamilyResolved(true);

    // Kalıcı auth profilini ek istek/polling üretmeden mevcut tek /auth/me
    // çağrısıyla arka planda tazele. Başarısız olması oluşturulan aileyi geri almaz.
    apiRequest("/auth/me").then((meData) => {
      if (!meData?.user) return;
      saveSession({ user: meData.user });
      const backendProfile = meData.user.profil || meData.user;
      setProfil((current) => ({
        ...(current || {}),
        ...backendProfile,
        familyId: sonuc.familyId,
        aktivFamilyId: sonuc.familyId,
        families: Array.isArray(backendProfile?.families) && backendProfile.families.length
          ? backendProfile.families
          : nextFamilies,
        aileYetkisi: sonuc.rol || "Aile Yöneticisi",
        permissions: sonuc.permissions || adminPermissions,
      }));
    }).catch(() => {});
  };

  /* ── Aile oluştur ── */
  const aileOlustur = async (onerilenAileAdi = "") => {
    setAileHata("");
    const olusturulacakAileAdi = aileAdi.trim() || String(onerilenAileAdi || "").trim();
    if (!olusturulacakAileAdi) { setAileHata(rt("family.error.nameRequired")); return false; }
    try {
      const sonuc = await aileOlusturService({ aileAdi: olusturulacakAileAdi, user });
      await aileUyelikSonrasiSenkronizeEt(sonuc);
      setSeciliAileEkrani("");
      setAileAdi("");
      setAktifSayfa("ana");
      setBasariMesaji(sonuc.familyName || olusturulacakAileAdi);
      logAnalyticsEvent("family_created", { source: "app" });
      return true;
    } catch (e) { setAileHata(e.message); return false; }
  };

  /* ── Aileye katıl ── */
  const aileyeKatil = async () => {
    setAileHata("");
    if (!katilmaKodu.trim()) { setAileHata(rt("family.error.codeRequired")); return; }
    try {
      const sonuc = await aileyeKatilService({ katilmaKodu, user });
      await aileUyelikSonrasiSenkronizeEt(sonuc);
      setSeciliAileEkrani("");
      setKatilmaKodu("");
      setAktifSayfa("ana");
      setBasariMesaji(sonuc.familyName || "");
      logAnalyticsEvent("family_joined", { source: "invite_code" });
    } catch (e) { setAileHata(e.message); }
  };

  /* ── Aileden ayrıl ── */
  const ailedenAyril = async () => {
    const activeMembers = (uyelerRef.current || uyeler || []).filter((uye) => (uye?.status || "active") === "active");
    const isLastMember = activeMembers.length <= 1;
    const currentUid = user?.uid || user?.id;
    const me = activeMembers.find((uye) => [uye?.uid, uye?.id].filter(Boolean).includes(currentUid));
    const activeAdmins = activeMembers.filter((uye) => isFamilyAdminRole(uye?.aileYetkisi || uye?.role));
    const isOnlyAdminLeaving = !isLastMember && isFamilyAdminRole(me?.aileYetkisi || me?.role || profil?.aileYetkisi) && activeAdmins.length <= 1;
    const confirmed = isLastMember
      ? await appConfirm(rt("family.confirmLeaveLastMember"), {
          title: rt("family.confirmLeaveLastMemberTitle"),
          confirmText: rt("family.confirmLeaveLastMemberButton"),
          danger: true,
        })
      : isOnlyAdminLeaving
        ? await appConfirm(rt("family.confirmLeaveOnlyAdmin"), {
            title: rt("family.confirmLeaveOnlyAdminTitle"),
            confirmText: rt("family.confirmLeaveOnlyAdminButton"),
            danger: true,
          })
        : await appConfirm(rt("family.confirmLeave"), { danger: true });
    if (!confirmed) return;
    try {
      const sonuc = await ailedenAyrilService({ user, currentFamilyId: familyId });
      // Ayrılma cevabı aynı zamanda yeni aktif aileyi belirler. Bu geçişi yalnızca
      // görünür state alanlarını değiştirerek yapmak activeFamilyRef'i eski ailede
      // bırakıyordu; yeni ailenin /members cevabı da bu yüzden gecikmiş/eski cevap
      // sanılıp ekrana uygulanmıyordu. Ortak snapshot commit'i state, ref ve cache'i
      // atomik tutar. Devamındaki yenileme deduplikasyonlu tek üyeler isteğidir.
      familyLoadSeqRef.current += 1;
      if (sonuc?.familyId) {
        commitFamilySnapshot(sonuc, sonuc.remainingFamilies || []);
        setLoadingFamily(false);
        await uyeleriYenile(sonuc.familyId);
      } else {
        activeFamilyRef.current = {
          familyId: null,
          familyName: "",
          familyCode: "",
          ownerUid: "",
        };
        presenceUsersRef.current = [];
        membersLoadedFamilyRef.current = "";
        setFamilyResolved(true);
        setFamilyId(null); setFamilyName(""); setFamilyCode(""); setOwnerUid("");
        setUyeler([]); uyelerRef.current = [];
        setMembersLoading(false);
        try {
          localStorage.removeItem("fp:aktivFamilyId");
          localStorage.removeItem("fp:familyCache");
        } catch {}
        setProfil((p) => ({
          ...p, familyId: null, families: [],
          aileYetkisi: "Aile Üyesi", permissions: defaultPermissions,
        }));
      }
      if (sonuc?.familyDeleted) {
        appAlert(rt("family.leftAndDeleted"), { type: "success" });
      }
      setSeciliUye(null);
      setAktifSayfa("ana");
    } catch (e) { appAlert(e.message); }
  };

  /* ── Kodu kopyala ── */
  const koduKopyala = async () => {
    try { await navigator.clipboard.writeText(familyCode); }
    catch { }
    appAlert(rt("family.codeCopied"));
  };

  /* ── Aile adı güncelle ── */
  const aileAdiGuncelle = async (yeniAd) => {
    if (!familyId || !yeniAd.trim()) return;
    await apiRequest(`/families/${familyId}`, {
      method: "PATCH",
      body: JSON.stringify({ name: yeniAd.trim() }),
    });
    setFamilyName(yeniAd.trim());
  };

  /* ── Üye yetki değiştir ── */
  const uyeYetkiDegistir = async (uyeId, yeniYetki) => {
    await uyeYetkiDegistirService({ uyeId, yeniYetki, familyId });
    const patch = { aileYetkisi: yeniYetki, role: yeniYetki };
    setUyeler((prev) => prev.map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, ...patch } : u));
    uyelerRef.current = (uyelerRef.current || []).map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, ...patch } : u);
    setSeciliUye((u) => u ? { ...u, ...patch } : u);
    if (uyeId === user?.uid) setProfil((p) => ({ ...p, aileYetkisi: yeniYetki }));
    await uyeleriYenile(familyId);
  };

  /* ── Üye rol değiştir ── */
  const uyeRolDegistir = async (uyeId, yeniRol) => {
    await uyeRolDegistirService({ uyeId, yeniRol, familyId });
    const patch = { rol: yeniRol, profilRol: yeniRol };
    setUyeler((prev) => prev.map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, ...patch } : u));
    uyelerRef.current = (uyelerRef.current || []).map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, ...patch } : u);
    setSeciliUye((u) => u ? { ...u, ...patch } : u);
    if (uyeId === user?.uid) setProfil((p) => ({ ...p, rol: yeniRol }));
    await uyeleriYenile(familyId);
  };

  /* ── Üyeyi aileden çıkar ── */
  const uyeyiAiledenCikar = async (uye) => {
    const uyeUid = uye?.uid || uye?.id;
    if (uyeUid === ownerUid) return appAlert(rt("family.error.ownerRemove"));
    const adSoyad = `${uye?.ad || ""} ${uye?.soyad || ""}`.trim() || uye?.email || rt("family.member");
    const onay = await appConfirm(rt("family.confirmRemove", { name: adSoyad }), { danger: true });
    if (!onay) return;
    await uyeyiAiledenCikarService({ uyeUid, familyId });
    setUyeler((prev) => prev.filter((u) => (u.uid || u.id) !== uyeUid));
    uyelerRef.current = (uyelerRef.current || []).filter((u) => (u.uid || u.id) !== uyeUid);
    setSeciliUye(null);
    await uyeleriYenile(familyId);
  };

  /* ── Yetki kaydet ── */
  const yetkiKaydet = async (uyeId, yeniPermissions) => {
    await yetkiKaydetService({ uyeId, yeniPermissions, familyId });
    setUyeler((prev) => prev.map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, permissions: yeniPermissions } : u));
    uyelerRef.current = (uyelerRef.current || []).map((u) => (u.uid === uyeId || u.id === uyeId) ? { ...u, permissions: yeniPermissions } : u);
    setSeciliYetkiUye(null);
    appAlert(rt("family.permissionsSaved"), { type: "success" });
    await uyeleriYenile(familyId);
  };

  /* ── Üye şifre sıfırla ── */
  const uyeSifreSifirla = async (uyeEmail) => {
    const cleanEmail = String(uyeEmail || "").trim();
    if (!cleanEmail) {
      const msg = rt("family.error.noUserEmail");
      appAlert(msg);
      throw new Error(msg);
    }

    try {
      await apiRequest("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ login: cleanEmail, lang: localStorage.getItem("appLanguage") || "tr" }),
      });
      appAlert(rt("family.passwordResetSentTo", { email: cleanEmail }), { type: "success" });
    } catch (err) {
      console.error("[useFamily] şifre sıfırlama e-postası gönderilemedi:", err?.code || err?.message);
      let msg = err?.message || rt("family.error.passwordResetFailed");
      appAlert(msg);
      throw new Error(msg);
    }
  };

  /* ── Üye renk güncelle ── */
  const uyeRenkGuncelle = async (uyeUid, renk, onSelfColorChange) => {
    try {
      await apiRequest(`/families/${familyId}/members/${uyeUid}`, {
        method: "PATCH",
        body: JSON.stringify({ color: renk, calendarColor: renk }),
      });
    } catch (e) { console.warn("[useFamily] renk güncellenemedi:", e.message); }
    // uyeler listesini güncelle — tüm sayfalar buradan okuyor
    setUyeler((prev) => prev.map((u) =>
      u.uid === uyeUid ? { ...u, calendarColor: renk, color: renk } : u
    ));
    onSelfColorChange?.(renk);
  };

  /* ── Borç hesabı değiştir ── */
  const borcHesabiDegistir = async () => {
    const yeni = !borcHesabi;
    setBorcHesabi(yeni);
    await borcHesabiDegistirService({ familyId, yeniDeger: yeni });
  };

  // Avatar/renk değişince profilGuncelle callback olarak bu fonksiyonu çağırır
  const uyeProfilGuncelle = ({ uid, avatar, calendarColor, ad, soyad, telefon, dogumTarihi }) => {
    setUyeler((prev) => {
      const next = prev.map((u) => {
        if (u.uid !== uid) return u;
        const updated = { ...u };
        if (avatar !== undefined)        updated.avatar = avatar;
        if (calendarColor !== undefined) { updated.calendarColor = calendarColor; updated.color = calendarColor; }
        if (ad !== undefined)            updated.ad = ad;
        if (soyad !== undefined)         updated.soyad = soyad;
        if (telefon !== undefined)       updated.telefon = telefon;
        if (dogumTarihi !== undefined)   updated.dogumTarihi = dogumTarihi;
        return updated;
      });
      uyelerRef.current = next;
      const cache = loadFamilyCache();
      if (String(cache?.familyId || "") === String(familyId || "")) {
        saveFamilyCache({ ...cache, uyeler: next });
      }
      return next;
    });
  };

  return {
    familyId, setFamilyId, familyContextUserUid, familyName, setFamilyName,
    familyCode, setFamilyCode, ownerUid, setOwnerUid,
    loadingFamily, setLoadingFamily,
    membersLoading,
    familyResolved, familyLoadError,
    seciliAileEkrani, setSeciliAileEkrani,
    aileAdi, setAileAdi, katilmaKodu, setKatilmaKodu,
    aileHata, setAileHata, basariMesaji, setBasariMesaji,
    uyeler, setUyeler, uyelerRef,
    seciliUye, setSeciliUye,
    uyelerAcik, setUyelerAcik,
    seciliYetkiUye, setSeciliYetkiUye,
    borcHesabi, setBorcHesabi,
    aileBilgisiYukle, switchFamily, activeFamilyRef,
    aileSifirla, uyelerDinle, uyeleriYenile, uyeProfilGuncelle,
    aileOlustur, aileyeKatil, ailedenAyril,
    koduKopyala, aileAdiGuncelle,
    uyeYetkiDegistir, uyeRolDegistir, uyeyiAiledenCikar,
    yetkiKaydet, uyeSifreSifirla, uyeRenkGuncelle,
    borcHesabiDegistir,
  };
}
